import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { CreateRealtimeToken as CREATE_REALTIME_TOKEN } from 'src/api/realtime.api';
import type { CreateRealtimeToken, CreateRealtimeTokenInput, CreateRealtimeTokenVariables } from 'types/graphql';

/**
 * Mints a subscription token for one realtime channel and topic set through createRealtimeToken.
 *
 * Callers keep `{ variables: { channel, topics } }` and read `data.getRealtimeToken` (the document aliases the
 * new root). `getRealtimeToken` is a `useCallback` over Apollo's stable mutate, so its identity is stable across
 * renders, which the callers rely on — it sits in the `useCallback` deps of the token function handed to
 * `useInngestRealtime`, and a new identity would tear the socket down and drop messages.
 */
export function useGetRealtimeToken() {
  const [mutate, { loading, error }] = useMutation<CreateRealtimeToken, CreateRealtimeTokenVariables>(
    CREATE_REALTIME_TOKEN
  );
  const getRealtimeToken = useCallback(
    ({ variables }: { variables: CreateRealtimeTokenInput }) => mutate({ variables: { input: variables } }),
    [mutate]
  );
  return { getRealtimeToken, loading, error };
}
